import type { HTMLAttributes } from 'react'
import { cn } from '../lib/cn'

/**
 * Reordenar itens de uma lista editável: duas ações em texto, "subir" e "descer", no mesmo desenho
 * das ações de linha (`RowActions`). Sem arrastar: a ordem muda um passo por vez, com o teclado e
 * com o toque, e cada passo é anunciado pelo rótulo acessível. Nas pontas a ação que não existe fica
 * desabilitada, nunca some: a lista não muda de forma conforme o item.
 */
export interface ReorderControlsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Posição do item, de 0 a `count - 1`. */
  index: number
  count: number
  /** Recebe a posição atual e a nova. */
  onMove: (from: number, to: number) => void
  /** Nome do item, para o rótulo acessível ("Mover 'Estilo musical' para cima"). */
  itemLabel?: string
  upLabel?: string
  downLabel?: string
}

const actionClassName =
  "font-body text-eyebrow text-label hover:text-ink disabled:text-label-soft disabled:hover:text-label-soft cursor-pointer font-light tracking-normal disabled:cursor-default max-desk:relative max-desk:after:absolute max-desk:after:inset-x-0 max-desk:after:top-1/2 max-desk:after:h-touch max-desk:after:-translate-y-1/2 max-desk:after:content-['']"

export function ReorderControls({
  index,
  count,
  onMove,
  itemLabel,
  upLabel = 'subir',
  downLabel = 'descer',
  className,
  ...props
}: ReorderControlsProps) {
  const name = itemLabel ? ` "${itemLabel}"` : ''
  return (
    <div
      data-slot="reorder-controls"
      className={cn('flex items-center gap-4', className)}
      {...props}
    >
      <button
        type="button"
        disabled={index <= 0}
        aria-label={`Mover${name} para cima`}
        onClick={() => onMove(index, index - 1)}
        className={actionClassName}
      >
        {upLabel}
      </button>
      <button
        type="button"
        disabled={index >= count - 1}
        aria-label={`Mover${name} para baixo`}
        onClick={() => onMove(index, index + 1)}
        className={actionClassName}
      >
        {downLabel}
      </button>
    </div>
  )
}
